import { Controller } from "@hotwired/stimulus"

/**
 * Hover/focus help panel for form-field explanations. Deliberately not
 * bootstrap.Tooltip: the panel is plain DOM styled by .help-tip__* so
 * the deferred Tailwind migration restyles one SCSS block instead of a
 * Bootstrap widget (docs/ui-conventions.md).
 *
 * The panel opens on hover or keyboard focus of the trigger and stays
 * open while focus/hover remains inside the wrapper, so a "Learn more"
 * link inside the panel is Tab-reachable. Escape closes it.
 *
 * @example
 *   <span class="help-tip"
 *         data-controller="help-tooltip"
 *         data-help-tooltip-content-value="What this field means."
 *         data-action="mouseenter->help-tooltip#show mouseleave->help-tooltip#hide
 *                      focusin->help-tooltip#show focusout->help-tooltip#hideOnFocusOut
 *                      keydown->help-tooltip#hideOnEscape">
 *     <button type="button" data-help-tooltip-target="trigger" aria-label="Help: Subject">…</button>
 *   </span>
 */
export default class extends Controller {
  static targets = ["trigger"]
  static values = { content: String, moreHref: String, moreLabel: String }

  disconnect() {
    if (this.panel) {
      this.panel.remove()
      this.panel = null
    }
  }

  show() {
    this.panel ||= this.buildPanel()
    this.panel.hidden = false
    this.triggerTarget.setAttribute("aria-describedby", this.panel.id)
  }

  hide() {
    if (!this.panel) return

    this.panel.hidden = true
    this.triggerTarget.removeAttribute("aria-describedby")
  }

  hideOnFocusOut(event) {
    if (this.element.contains(event.relatedTarget)) return

    this.hide()
  }

  hideOnEscape(event) {
    if (event.key === "Escape") this.hide()
  }

  buildPanel() {
    const panel = document.createElement("div")
    panel.className = "help-tip__panel"
    panel.setAttribute("role", "tooltip")
    panel.id = `help-tip-${crypto.randomUUID()}`
    panel.hidden = true

    const text = document.createElement("p")
    text.className = "help-tip__text"
    text.textContent = this.contentValue
    panel.appendChild(text)

    if (this.moreHrefValue) {
      const link = document.createElement("a")
      link.className = "help-tip__more"
      link.href = this.moreHrefValue
      link.textContent = this.moreLabelValue
      panel.appendChild(link)
    }

    this.element.appendChild(panel)
    return panel
  }
}
